// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ChatContext.tsx
 * @input Uses React createContext
 * @output Exports Chat message/list/composer/layout contexts. The composer
 *   context (value + input-control registration) is public composition API,
 *   re-exported from Chat/index.ts for custom inputs.
 * @position Shared contexts; consumed by ChatMessage, ChatComposer,
 *   ChatComposerInput, ChatSendButton, and custom inputs.
 */
import { createContext, use } from 'react';
export const ChatMessageContext = /*#__PURE__*/createContext(null);
ChatMessageContext.displayName = 'ChatMessageContext';
export function useChatMessageContext() {
  return use(ChatMessageContext);
}
export const ChatListContext = /*#__PURE__*/createContext(null);
ChatListContext.displayName = 'ChatListContext';
export function useChatListContext() {
  return use(ChatListContext);
}

// =============================================================================
// Composer context — shared state between ChatComposer and ChatComposerInput
// =============================================================================

/**
 * Imperative surface the composer shell can invoke on its input slot.
 *
 * The input is one slot inside the composer body — it does not span the whole
 * body — so shell-level interactions like "click empty space to focus the
 * input" must flow shell → input. A custom input registers this control (via
 * `ChatComposerContextValue.inputControlRef`) so the shell can drive it
 * without knowing its DOM shape. Optional methods can be added over time;
 * inputs implement only what they support.
 */

export const ChatComposerContext = /*#__PURE__*/createContext(null);
ChatComposerContext.displayName = 'ChatComposerContext';
export function useChatComposerContext() {
  return use(ChatComposerContext);
}

// =============================================================================
// Layout context — shared between ChatLayout and ChatMessageList
// =============================================================================

export const ChatLayoutContext = /*#__PURE__*/createContext(null);
ChatLayoutContext.displayName = 'ChatLayoutContext';
export function useChatLayoutContext() {
  return use(ChatLayoutContext);
}